import AxiosInstance from "@/config/AxiosInstance";
import { API_BASE_URL, API_BASE_URL_ADMIN } from "@/config/appConfig";

type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

type LocationListParams = {
  brandId?: QueryValue;
  userId?: QueryValue;
  page?: QueryValue;
  row?: QueryValue;
  searchKey?: QueryValue;
  groupId?: QueryValue;
  participantId?: QueryValue;
  sortBy?: QueryValue;
  sortByDir?: QueryValue;
};

type ActiveParticipantParams = Record<string, QueryValue>;
type RequestPayload = Record<string, unknown>;
type IdValue = string | number;

const toQueryValue = (value: QueryValue) => {
  if (Array.isArray(value)) return value.join(",");
  return value;
};

export const locationManagementService = {
  getLocationManagementList({
    brandId,
    userId,
    page,
    row,
    searchKey,
    groupId,
    participantId,
    sortBy,
    sortByDir,
  }: LocationListParams) {
    return AxiosInstance.get(`${API_BASE_URL_ADMIN}/locationReportListing`, {
      params: {
        id: brandId,
        userId,
        page,
        row,
        searchKey,
        groupId: toQueryValue(groupId),
        participantId: toQueryValue(participantId),
        sortBy,
        sortByDir,
      },
    });
  },

  getLocationDetail(locationId: IdValue, brandId: IdValue) {
    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/location-detail`,
      { params: { fk_location_id: locationId, brand_id: brandId } }
    );
  },

  updateLocation(locationId: IdValue, payload: RequestPayload) {
    return AxiosInstance.put(
      `${API_BASE_URL}/location-management/locations`,
      payload
    );
  },

  updateLocationStatus(payload: RequestPayload) {
    return AxiosInstance.post(
      `${API_BASE_URL}/location-management/location-status`,
      payload
    );
  },

  getLocationStatusHistory(brandId: IdValue, locationId: IdValue) {
    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/location-status-history`,
      { params: { brand_id: brandId, location_id: locationId } }
    );
  },

  createLocation(payload: RequestPayload) {
    return AxiosInstance.post(
      `${API_BASE_URL}/location-management/locations`,
      payload
    );
  },

  getCountryList() {
    return AxiosInstance.get(`${API_BASE_URL}/location-management/countries`);
  },

  getStateList(country: string) {
    return AxiosInstance.get(`${API_BASE_URL}/location-management/states`, {
      params: { country },
    });
  },

  getGroupList(brandId: IdValue) {
    return AxiosInstance.get(`${API_BASE_URL}/location-management/groups`, {
      params: { brand_id: brandId },
    });
  },

  getGroupAndGroupTypes(brandId: IdValue) {
    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/group-types`,
      {
        params: { brand_id: brandId },
      }
    );
  },

  getParticipantDetail(brandId: IdValue, participantId: IdValue) {
    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/participant-detail`,
      { params: { brand_id: brandId, fk_participant_id: participantId } }
    );
  },

  getActiveParticipants(
    brandId?: IdValue | ActiveParticipantParams,
    { page, row }: { page?: QueryValue; row?: QueryValue } = {}
  ) {
    const params =
      typeof brandId === "object" ? brandId : { brand_id: brandId, page, row };

    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/participants`,
      { params }
    );
  },

  lookupZipCode(zip: string, country: string) {
    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/zip-city-state`,
      { params: { zip_code: zip, country } }
    );
  },

  exportLocationsXlsx(
    brandId: IdValue,
    groupIds: IdValue[] = [],
    participantIds: IdValue[] = []
  ) {
    return AxiosInstance.get(
      `${API_BASE_URL}/location-management/locations-export`,
      {
        params: {
          brand_id: brandId,
          group_ids: toQueryValue(groupIds),
          participant_ids: toQueryValue(participantIds),
        },
        responseType: "blob",
      }
    );
  },
};
